import { ActionReducer, INIT, UPDATE } from '@ngrx/store';
import { defaultMergeReducer, localStorageSync, rehydrateApplicationState } from 'ngrx-store-localstorage';

import { getEnvironment } from '../features/shared/utils/get-environment';
import { resolveTabScope } from '../features/shared/utils/tab-workspace-storage';

interface CustomOptions {
  serialize?: (value: string) => any;
  deserialize?: (value: string) => any;
  encrypt?: (value: string) => string;
  decrypt?: (value: string) => string;
}

export interface LocalStorageSyncConfig<T> {
  keys: (keyof T | Partial<Record<keyof T, CustomOptions>>)[];
  rehydrate?: boolean;
  storage?: Storage;
  removeOnUndefined?: boolean;
  restoreDates?: boolean;
  storageKeySerializer?: (key: string) => string;
  syncCondition?: (state: T) => any;
  checkStorageAvailability?: boolean;
  mergeReducer?: (state: T, rehydratedState: any, action: any) => any;
}

export function createLocalStorageSyncReducer<T>(config: LocalStorageSyncConfig<T>) {
  return (reducer: ActionReducer<any>): ActionReducer<any> => {
    return localStorageSync(config as any)(reducer);
  };
}

/**
 * A `storageKeySerializer` that namespaces the key by the tab's workspace, for state that belongs
 * to one workspace rather than to the user.
 *
 * `localStorage` is shared by every tab, so an unscoped key lets two tabs on different workspaces
 * overwrite each other, and rehydrates entity ids (a checklist, a saved filter) into a workspace
 * that does not contain them. Keys fall back to the unscoped shape before a workspace is known,
 * which is also what `sessionStorage`-backed state keeps using: that is per-tab already.
 */
export function workspaceScopedKeySerializer(prefix = ''): (key: string) => string {
  return (key: string): string => {
    const workspaceId = resolveTabScope()?.workspaceId;

    return `semeris-${getEnvironment()}-${workspaceId ? `${workspaceId}-` : ''}${prefix}${key}`;
  };
}

/**
 * Like {@link createLocalStorageSyncReducer}, for state that belongs to one workspace: keys are
 * namespaced by {@link workspaceScopedKeySerializer} and storage is re-read on every rehydrate.
 *
 * `localStorageSync` reads storage once, when it is built, and merges that same snapshot back in on
 * every `INIT`. A same-tab workspace switch replays `INIT` (`changeAssetFactory`), so the snapshot
 * taken for the workspace being left would be merged into the reset store and then written straight
 * back out under the destination workspace's keys. Reading per rehydrate gives each workspace its
 * own state instead.
 */
export function createWorkspaceScopedSyncReducer<T>(config: LocalStorageSyncConfig<T>, keyPrefix = '') {
  const storageKeySerializer = workspaceScopedKeySerializer(keyPrefix);

  return createLocalStorageSyncReducer<T>({
    ...config,
    storageKeySerializer,
    mergeReducer: (state: T, _staleSnapshot: any, action: any) =>
      action?.type === INIT || action?.type === UPDATE
        ? defaultMergeReducer(
            state,
            rehydrateApplicationState({
              keys: config.keys as any,
              storage: config.storage,
              storageKeySerializer,
              restoreDates: config.restoreDates ?? true,
            }),
            action,
          )
        : state,
  });
}
